import { Box, Container, Grid, Typography } from "@mui/material";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../AuthContext";
import invoices from "../assets/invoices.svg";
import agreements from "../assets/agreements.svg";
import revenue from "../assets/revenue.svg";
import DealerInfo from "../sections/dealerdetails/DealerInfo";
import DealerCards from "../sections/dealerdetails/DealerCards";
import TenantsList from "../components/TenantsList";

import { getTenantByfDealerId } from "../services/tenantServices";

import { checkDealerOnboard, getDealerDetailsCardData, verifyDealer } from "../services/dealerServices";
import { linkAction, parseDealerLink } from "../utils/deepLink";
import { isVendorDealer } from "../utils/vendorDealer";
import AgreementsClaimsTabs from "../components/AgreementsClaimsTabs";
import JBWReleasaNotesDialog from "../components/JBWReleasaNotesDialog";
import { useLocation, useNavigate } from "react-router-dom";

const DealerDetails = () => {
  const { user, dealer, group, APP_ENV, setDealer } = useContext(AuthContext);
  let urlLocation = useLocation();
  const navigate = useNavigate();
  // A link into this page, e.g. from the dealer opt-in email (#419): which tab, tenant and filter
  // to open on. utils/deepLink.js validates it. It is consumed once, below: turned into the same
  // navigation state in-app navigation uses (state.key, state.initialFilter) plus the tenant to
  // preselect, and the query is cleared -- so a reset, a tab switch or a remount never re-applies
  // it.
  const link = parseDealerLink(urlLocation.search);
  console.log("urlLocation :", urlLocation?.state?.key);
  const [activeTab, setActiveTab] = useState(0);
  const [tenantList, setTenantList] = useState([]);
  const [cardsData, setCardsData] = useState(null);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [vendorCheck, setVendorCheck] = useState(null);
  // null until this dealer's onboarding row has loaded, so a vendor dealer never flashes the
  // ServiceTitan UI. Keyed by dealer: on a switch, the previous dealer's answer doesn't count.
  const isVendor =
    vendorCheck && vendorCheck.dealerId === selectedDealer?.dealerId ? vendorCheck.isVendor : null;
  const locDealer = localStorage.getItem("dealer")
    ? JSON.parse(localStorage.getItem("dealer"))
    : null;
  console.log("Context Dealer data :", dealer);

  useEffect(() => {
    if (urlLocation?.state?.key) {
      setActiveTab(urlLocation?.state?.key);
    } else {
      setActiveTab(0);
    }
  }, [urlLocation?.state?.key]);

  // Apply the link as navigation state and clear it from the URL; or drop it.
  const consumeLink = (applies) =>
    navigate(urlLocation.pathname, {
      replace: true,
      state: applies
        ? { key: link.tabKey, initialFilter: link.filter, initialFilterTab: link.tab, tenantId: link.tenantId }
        : undefined,
    });

  const switching = useRef(false);
  useEffect(() => {
    const action = linkAction({ link, group, currentDealerId: locDealer?.dealerId });
    if (action === "none") {
      setSelectedDealer(locDealer);
    } else if (action === "apply") {
      consumeLink(true);
    } else if (action === "switch") {
      // As Dealer Summary does -- and nothing is loaded for the previous dealer meanwhile, so its
      // tenants can never arrive late under the new dealer's name.
      if (switching.current) return;
      switching.current = true;
      verifyDealer(link.dealerId)
        .then((res) => {
          const linked = res.data?.[0];
          if (linked) {
            localStorage.setItem("dealer", JSON.stringify(linked));
            setDealer(linked);
          }
          consumeLink(Boolean(linked));
        })
        .catch((err) => {
          console.error("Unable to open the linked dealer", err);
          consumeLink(false);
        })
        .finally(() => {
          switching.current = false;
        });
    } else if (action === "drop") {
      // Tab, filter and tenant all ignored, rather than show another dealer's list under them.
      consumeLink(false);
    }
    // "wait": the user's group decides whose link this is; the effect runs again when it loads.
  }, [dealer?.dealerId, group, urlLocation.search]);
  // useEffect(() => {
  //   setSelectedDealer((values) => JSON.parse(localStorage.getItem("dealer")));
  // }, []);
  console.log("Dealer Details dealer:", selectedDealer);
  // console.log("Dealer Details user:", user);
  const trmRef = useRef();
  const onSync = () => {
    trmRef.current.scrollIntoView({
      behavior: "smooth",
    });
  };

  const getTenants = () => {
    console.log("loading tenant list");
    setTenantList([]);
    setSelectedTenant(null);
    getTenantByfDealerId(selectedDealer.dealerId)
      .then(
        (res) => {
          console.log("getTenantByfDealerId response  :", res);
          const data = res.data;
          if (data && data.length > 0) {
            if (
              selectedDealer.tenantId &&
              selectedDealer.tenantId !== "undefined" &&
              selectedDealer.tenantId?.length > 0
            ) {
              // console.log("adding tenants filter : ",dealer.tenants)
              setTenantList(
                data.filter((dt) => selectedDealer.tenantId?.includes(dt.tenantId))
              );
            } else {
              // console.log("Setting Tenant list not dealer tenants :",data);
              setTenantList(data);
            }
          }
        },
        (err) => {
          console.log("Unable retrive tenats data", err);
        }
      )
      .catch((err) => {
        console.error("tenant service Error :", err);
      });
  };

  const getCardsData = () => {
    getDealerDetailsCardData(selectedDealer.dealerId)
      .then(
        (res) => {
          console.log("cards data :", res);
          const data = APP_ENV === "Development" ? res.data.body : res.data;
          const statusCode = res.status;
          if (statusCode === 200 && data.length > 0) {
            setCardsData(data[0]);
          }
        },
        (err) => {
          console.log("Unable retrive cards data", err);
        }
      )
      .catch((err) => {
        console.error("CardsData service Error :", err);
      });
  };

  useEffect(() => {
    if (selectedDealer) {
      getTenants();
      getCardsData();
    }
  }, [selectedDealer?.dealerId]);

  useEffect(() => {
    const dealerId = selectedDealer?.dealerId;
    if (!dealerId) return undefined;
    let stale = false;
    checkDealerOnboard(dealerId)
      .then((res) => {
        if (!stale) setVendorCheck({ dealerId, isVendor: isVendorDealer(res.data) });
      })
      .catch((err) => {
        console.error("Unable to load the dealer's onboarding row", err);
        // Fails open on purpose: a failed read must not take the Tenants section and New Agreement
        // away from every ServiceTitan dealer. A vendor dealer sees them until the next load. No
        // server check backs this for New Agreement, so this hides the button and nothing more.
        if (!stale) setVendorCheck({ dealerId, isVendor: false });
      });
    return () => {
      stale = true;
    };
  }, [selectedDealer?.dealerId]);

  // Memoized: AgreementsGrid rebuilds its columns whenever this object changes.
  const tabsDealer = useMemo(
    () => selectedDealer && { ...selectedDealer, isVendorDealer: isVendor },
    [selectedDealer, isVendor]
  );

  const handleSelectedTenant = (param) => {
    setSelectedTenant(param);
  };
  const handleRestTenant = () => {
    console.log("reset tenant called");
    setSelectedTenant(null);
    getTenants();
  };

  return (
    <Box component="main">
      <Container maxWidth="xl" spacing={1}>
        {!selectedDealer ? (
          <Box>
            <Typography component="h3" variant="h3">
              Dealer Is required
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} lg={6}>
              <DealerInfo
                dealer={selectedDealer}
                user={user}
                onSync={onSync}
                sx={{ maxWidth: "100%", height: "90%", borderRadius: "10px" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={2}>
              <DealerCards
                value={
                  cardsData?.totalInvoicesProcessed
                    ? cardsData?.totalInvoicesProcessed
                    : 0
                }
                imgsrc={invoices}
                titletxt={"Total Invoices Processed"}
                sx={{ maxWidth: "100%", height: "90%", borderRadius: "10px" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={2}>
              <DealerCards
                value={
                  cardsData?.agreementsCreated
                    ? cardsData?.agreementsCreated
                    : 0
                }
                imgsrc={agreements}
                titletxt={"Agreements Created"}
                sx={{ maxWidth: "100%", height: "90%", borderRadius: "10px" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={2}>
              <DealerCards
                value={
                  cardsData?.totalClaims ? `${cardsData?.totalClaims}` : `0`
                }
                imgsrc={revenue}
                titletxt={"Claims Created"}
                sx={{ maxWidth: "100%", height: "90%", borderRadius: "10px" }}
              />
            </Grid>
            {isVendor === false && !["workiz", "sera"].includes(
              selectedDealer.crmSystem?.toLowerCase()
            ) && (
                <Grid item xs={12} sm={12} lg={12}>
                  <TenantsList
                    tenantList={tenantList}
                    preselectTenantId={urlLocation.state?.tenantId}
                    updateTenantsList={getTenants}
                    onSelectedTenant={handleSelectedTenant}
                    onRestTenant={handleRestTenant}
                  />
                </Grid>
              )}
            <Grid item xs={12} sm={12} lg={12}>
              {/* <AgreementList displayType="dealers" dealer={selectedDealer} /> */}
              {/* {selectedDealer && ( */}
              <AgreementsClaimsTabs
                displayType="dealers"
                dealer={tabsDealer}
                showDealer={false}
                tenant={selectedTenant}
                activeTab={activeTab}
              />
              {/* )} */}
            </Grid>
            {/* <Grid item xs={12} sm={12} lg={12} ref={trmRef}>
              <TransmissionDetailsList
                displayType="dealers"
                dealer={selectedDealer}
              />
            </Grid> */}
            {/* <Grid item xs={12} sm={6} lg={3} ref={trmRef}>
              <TransmissionDetails />
            </Grid> */}
            {group === "jbw-dealer" && <JBWReleasaNotesDialog />}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default DealerDetails;
